import {
  MATERIALITY_BASIS_FIELDS,
  type SemanticFormatSpec,
  type SemanticMaterialityBasis,
  type SemanticMaterialitySpec,
} from "@/features/data-hub/model/semanticFormat";

/** Form state for the Measurement section shared by the measure and metric edit
 * dialogs. Every field is a string because these are text inputs; the build helpers
 * below are the single place where strings become the typed spec sent to the API. */
export type SemanticMeasurementFormValues = {
  unitType: string;
  unitSymbol: string;
  decimalPlaces: string;
  multiplier: string;
  materialityBasis: SemanticMaterialityBasis;
  minAbsDelta: string;
  minLevel: string;
  minPercentChange: string;
  minShareOfTotal: string;
  normalLow: string;
  normalHigh: string;
  materialityNotes: string;
};

/** The unit types the backend registry ships with. The registry is deliberately open
 * (``unit_type`` is a plain string, not an enum) — these seed the combobox, and free
 * text must stay accepted. */
export const UNIT_TYPE_SUGGESTIONS = [
  "currency",
  "percentage",
  "count",
  "duration_ms",
  "ratio",
] as const;

function numberToInput(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

function inputToNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function createEmptyMeasurementFormValues(): SemanticMeasurementFormValues {
  return {
    unitType: "",
    unitSymbol: "",
    decimalPlaces: "",
    multiplier: "",
    materialityBasis: "auto",
    minAbsDelta: "",
    minLevel: "",
    minPercentChange: "",
    minShareOfTotal: "",
    normalLow: "",
    normalHigh: "",
    materialityNotes: "",
  };
}

export function measurementToFormValues(
  format: SemanticFormatSpec | undefined,
  materiality: SemanticMaterialitySpec | undefined,
): SemanticMeasurementFormValues {
  const unitType = (format?.unit_type ?? "").trim();
  return {
    // "unknown" is the schema default, not a declaration — show it as unset so a
    // user isn't asked to clear a value nobody chose.
    unitType: unitType === "unknown" ? "" : unitType,
    unitSymbol: (format?.unit_symbol ?? "").trim(),
    decimalPlaces: numberToInput(format?.decimal_places),
    multiplier: numberToInput(format?.multiplier),
    materialityBasis: materiality?.basis ?? "auto",
    minAbsDelta: numberToInput(materiality?.min_abs_delta),
    minLevel: numberToInput(materiality?.min_level),
    minPercentChange: numberToInput(materiality?.min_percent_change),
    minShareOfTotal: numberToInput(materiality?.min_share_of_total),
    normalLow: numberToInput(materiality?.normal_low),
    normalHigh: numberToInput(materiality?.normal_high),
    materialityNotes: (materiality?.notes ?? "").trim(),
  };
}

/** The format spec to persist, or undefined to clear it.
 *
 * A unit type is what makes a format meaningful — without one there is nothing to
 * declare, so an empty unit type clears the whole spec rather than persisting a
 * symbol attached to nothing. */
export function buildFormatFromMeasurementValues(
  values: SemanticMeasurementFormValues,
): SemanticFormatSpec | undefined {
  const unitType = values.unitType.trim();
  if (!unitType) {
    return undefined;
  }
  const decimalPlaces = inputToNumber(values.decimalPlaces);
  const multiplier = inputToNumber(values.multiplier);
  return {
    unit_type: unitType,
    unit_symbol: values.unitSymbol.trim() || null,
    decimal_places: decimalPlaces === null ? null : Math.trunc(decimalPlaces),
    multiplier: multiplier === null ? 1 : multiplier,
    // Anything set through this dialog is a human decision.
    is_curated: true,
  };
}

const MATERIALITY_FIELD_INPUTS = {
  min_abs_delta: "minAbsDelta",
  min_level: "minLevel",
  min_percent_change: "minPercentChange",
  min_share_of_total: "minShareOfTotal",
  normal_low: "normalLow",
  normal_high: "normalHigh",
} as const satisfies Record<string, keyof SemanticMeasurementFormValues>;

/** The materiality spec to persist, or undefined to clear it.
 *
 * Only the chosen basis's own fields are read, so switching basis never submits a
 * stale threshold from the previous one — which would otherwise be rejected by the
 * backend validator, or worse, silently persisted alongside the new basis. */
export function buildMaterialityFromMeasurementValues(
  values: SemanticMeasurementFormValues,
): SemanticMaterialitySpec | undefined {
  const basis = values.materialityBasis;
  const notes = values.materialityNotes.trim();
  if (basis === "auto") {
    // "auto" is not a rejection, it's silence. With no notes there is nothing worth
    // persisting; with notes, the reason someone chose to leave it undeclared is.
    return notes ? { basis: "auto", notes, is_curated: true } : undefined;
  }

  const spec: SemanticMaterialitySpec = { basis, is_curated: true };
  let hasThreshold = false;
  for (const field of MATERIALITY_BASIS_FIELDS[basis]) {
    const inputKey = MATERIALITY_FIELD_INPUTS[field as keyof typeof MATERIALITY_FIELD_INPUTS];
    const parsed = inputToNumber(values[inputKey]);
    if (parsed !== null) {
      spec[field as keyof typeof MATERIALITY_FIELD_INPUTS] = parsed;
      hasThreshold = true;
    }
  }
  if (!hasThreshold) {
    // Every non-auto basis requires at least one of its own fields; sending a bare
    // basis would fail backend validation and lose the whole edit.
    return undefined;
  }
  if (notes) {
    spec.notes = notes;
  }
  return spec;
}

/** True when the low/high band is inverted — the one rule the backend enforces that a
 * user can trip while typing, so it is worth catching in the form. */
export function measurementRangeIsInverted(
  values: SemanticMeasurementFormValues,
): boolean {
  if (values.materialityBasis !== "range") {
    return false;
  }
  const low = inputToNumber(values.normalLow);
  const high = inputToNumber(values.normalHigh);
  return low !== null && high !== null && low > high;
}
